import { capitalize } from '@shared/utils';
import { head } from 'lodash-es';
import moment from 'moment';
import { Presenter } from '../../presenters/presenter';
import { AddressPresenter } from '../addresses/address-presenter';

export class PersonPresenter extends Presenter {

    get avatar() {
        return this._get('avatar', () => this.data.avatar_url || '/img/person-avatar-placeholder.png');
    }

    get addresses() {
        return this._get('addresses', () => AddressPresenter.create(this.get('addresses.data')));
    }

    get preferWorkEmailAddress() {
        return this._get('preferWorkEmailAddress', () => {
            return this.get('email_work') || this.get('email');
        });
    }

    get preferPersonalEmailAddress() {
        return this._get('preferPersonalEmailAddress', () => {
            return this.get('email') || this.get('email_work');
        });
    }

    get firstAddress() {
        return this._get('firstAddress', () => head(this.addresses));
    }

    get preferredAddress() {
        return this._get('preferredAddress', () => AddressPresenter.create(this.get('preferred_address.data')));
    }

    get email() {
        return this.data.email;
    }

    get firstName() {
        return this.data.first_name;
    }

    get dateOfBirth() {
        return this._get('dateOfBirth', () => {
            const m = moment(this.data.date_of_birth);

            if (m.isValid()) {
                return m.format('L');
            }
        });
    }

    get age() {
        return this._get('age', () => {
            const m = moment(this.data.date_of_birth);

            if (m.isValid()) {
                return moment().diff(m, 'years');
            }
        });
    }

    get hasBirthdayToday() {
        const now = moment().local();

        return this._get(`hasBirthdayToday:${now.format('YYYY-MM-DD')}`, () => {
            const birthday = moment(this.data.date_of_birth);

            if (birthday.isValid()) {
                return now.format('MM-DD') === birthday.format('MM-DD');
            }
        });
    }

    get gender() {
        switch (this.data.gender) {
            case 'm':
                return 'Male';

            case 'f':
                return 'Female';

            case 'other':
                return this.data.gender_other;
        }

        return undefined;
    }

    get fullName() {
        return [this.data.title, this.data.first_name, this.data.initials, this.data.last_name]
            .filter(Boolean)
            .join(' ');
    }

    get fullNameLastFirst() {
        const lastName = this.data.last_name ? this.data.last_name + ', ' : undefined;

        return [lastName, this.data.title, this.data.first_name, this.data.initials].filter(value => !!value).join(' ');
    }

    get fullNameWithPreferred() {
        return this._get('fullNameWithPreferred', () => {
            const preferredName = this.data.preferred_name;
            
            if (preferredName && preferredName !== this.data.first_name) {
                return `${this.fullName} (${preferredName})`
            }
            
            return this.fullName;
        });
    }

    get homeNumber() {
        return this.data.tel_home;
    }

    get initials() {
        const firstInitial = this.data.first_name.substring(0, 1);
        const lastInitial = this.data.last_name.substring(0, 1);

        return firstInitial + lastInitial;
    }

    get initialsLastFirst() {
        const firstInitial = this.data.first_name.substring(0, 1);
        const lastInitial = this.data.last_name.substring(0, 1);

        return lastInitial + ', ' + this.data.title + ' ' + firstInitial;
    }

    get lastName() {
        return this.data.last_name;
    }

    get mobileNumber() {
        return this.data.tel_mobile;
    }

    get faxNumber() {
        return this.data.tel_fax;
    }

    get name() {
        return this.nameNormal;
    }

    get nameShort() {
        return this.nameNormal;
    }

    get nameLastFirst() {
        return `${this.data.last_name}, ${this.data.first_name}`;
    }

    get nameNormal() {
        return `${this.data.first_name} ${this.data.last_name}`;
    }

    get workNumber() {
        return this.data.tel_work;
    }

    get religion() {
        return this.data.religion;
    }

    get ethnicity() {
        return this.data.ethnicity;
    }

    get comments() {
        return this.data.comments;
    }

    get nationality() {
        return this._get('nationality', () => (this.data.nationality || []).join(', '));
    }

    get language() {
        return this._get('language', () => (this.data.language || []).join(', '));
    }

    get roles() {
        return this._get('roles', () => {
            const personRoles = this.get('roles.data', []);
            const userRoles = this.get('user.data.roles.data', []).map(role => role.id);

            const roles = [...new Set([...personRoles, ...userRoles])].map(role => `${role.replace(/_/g, ' ')}`);

            roles.sort();

            return capitalize(roles.join(', '));
        });
    }

    get rolesHtml() {
        return this._get('rolesHtml', () => {
            return this.get('roles.data', [])
                .map(role => `
                    <span class="badge badge-small">
                        ${role.replace(/_/g, ' ')}
                    </span>
                `).join('');
        });
    }
}
